/**
 * Hold geometry and slicing (W1-06; R§4.6, S§3.4, F§11.2).
 *
 * In the hold's local frame (origin on the line at the note x, +y away from the line, mirrored for below notes):
 *   lineH  = L.height(t) * spd / ar
 *   bottom = t >= time ? 0 : height * spd / ar - lineH          head "eaten" at the line once started
 *   top    = endHeight * spd / ar - lineH
 *   [A0, A1] = holdAtlas (holdAtlasMH when multi-highlighted): tail rows at the image top, head rows at the bottom
 *   body   y in [bottom, top] (skipped when top < bottom), UV v in [A0/TH, 1 - A1/TH];
 *          holdRepeat: separate body texture (slot holdBody / holdBodyMh), v in [0, (top - bottom)/(2w) * bw/bh]
 *          with v = 0 at the far (tail) end
 *   head   only while t < time or with holdKeepHead; height 2w * A1/TW;
 *          holdCompact ? centred on bottom : y in [bottom - hh, bottom]
 *   tail   height 2w * A0/TW; holdCompact ? centred on top : y in [top, top + th]
 *   push order body, head, tail; all inside Translate(x, yOff/ar) * Scale(sx, sy) (no incline, no posControl;
 *   with noteUniformScale the length and the head/tail heights scale by size * ctrlSize too).
 * UV convention of NoteQuadBatch: v0 is the TOP of the source rect and maps to the far edge (flip_y).
 */

import type { FrameEnv, LineEnv, NoteEval } from './env';
import { SLOT_HOLD, SLOT_HOLD_BODY, SLOT_HOLD_BODY_MH, SLOT_MH_OFFSET, writeCorners } from './env';
import { quadOffscreen } from './geometry';

const fr = Math.fround;

/** Vertical extents (local y) of the three hold parts; exported for tests and overlays. */
export interface HoldExtents {
  bottom: number;
  top: number;
  /** Whether the body is drawn (top >= bottom). */
  body: boolean;
  /** Whether the head is drawn (t < time or holdKeepHead). */
  head: boolean;
  headY0: number;
  headY1: number;
  tailY0: number;
  tailY1: number;
}

/** Computes the hold's local extents (before Scale(sx, sy)). */
export function holdExtents(
  fe: FrameEnv,
  le: LineEnv,
  i: number,
  spd: number,
  scale: number,
  mh: boolean,
  out: HoldExtents,
): HoldExtents {
  const rt = le.rt;
  const skin = fe.skin;
  const lineH = fr(fr(le.lineHeight * spd) / fe.ar);
  const started = fe.t >= (rt.time[i] ?? 0);
  const bottom = started ? 0 : fr(fr(fr((rt.height[i] ?? 0) * spd) / fe.ar) - lineH);
  const top = fr(fr(fr((rt.endHeight[i] ?? 0) * spd) / fe.ar) - lineH);
  const [a0, a1] = mh ? skin.holdAtlasMH : skin.holdAtlas;
  const [tw] = mh ? skin.tex.hold_mh : skin.tex.hold;
  const headH = (2 * scale * a1) / tw;
  const tailH = (2 * scale * a0) / tw;
  out.bottom = bottom;
  out.top = top;
  out.body = top - bottom >= 0;
  out.head = !started || skin.holdKeepHead;
  out.headY0 = skin.holdCompact ? bottom - headH / 2 : bottom - headH;
  out.headY1 = out.headY0 + headH;
  out.tailY0 = skin.holdCompact ? top - tailH / 2 : top;
  out.tailY1 = out.tailY0 + tailH;
  return out;
}

const scratch: HoldExtents = {
  bottom: 0,
  top: 0,
  body: false,
  head: false,
  headY0: 0,
  headY1: 0,
  tailY0: 0,
  tailY1: 0,
};

/** Fills `out` with the hold's quads (body, head, tail in push order) and its outline. */
export function holdQuads(
  fe: FrameEnv,
  le: LineEnv,
  i: number,
  spd: number,
  scale: number,
  mh: boolean,
  fy: number,
  sx: number,
  sy: number,
  yOffAr: number,
  out: NoteEval,
): void {
  const rt = le.rt;
  const skin = fe.skin;
  const ex = holdExtents(fe, le, i, spd, scale, mh, scratch);
  const tx = rt.x[i] ?? 0;
  const [a0, a1] = mh ? skin.holdAtlasMH : skin.holdAtlas;
  const [, th] = mh ? skin.tex.hold_mh : skin.tex.hold;
  const holdSlot = SLOT_HOLD + (mh ? SLOT_MH_OFFSET : 0);
  let q = 0;
  let lo = Infinity;
  let hi = -Infinity;
  if (ex.body) {
    writeCorners(le, fy, tx, yOffAr, sx, sy, -scale, scale, ex.bottom, ex.top, out.pos, q * 8);
    if (skin.holdRepeat) {
      const [bw, bh] = mh ? skin.tex.holdBodyMh : skin.tex.holdBody;
      out.slot[q] = mh ? SLOT_HOLD_BODY_MH : SLOT_HOLD_BODY;
      out.uv[q * 4] = 0;
      out.uv[q * 4 + 1] = 0;
      out.uv[q * 4 + 2] = 1;
      out.uv[q * 4 + 3] = (((ex.top - ex.bottom) / (2 * scale)) * bw) / bh;
    } else {
      out.slot[q] = holdSlot;
      out.uv[q * 4] = 0;
      out.uv[q * 4 + 1] = a0 / th;
      out.uv[q * 4 + 2] = 1;
      out.uv[q * 4 + 3] = 1 - a1 / th;
    }
    out.offscreen[q] = quadOffscreen(out.pos, q * 8) ? 1 : 0;
    lo = Math.min(lo, ex.bottom);
    hi = Math.max(hi, ex.top);
    q++;
  }
  if (ex.head) {
    writeCorners(le, fy, tx, yOffAr, sx, sy, -scale, scale, ex.headY0, ex.headY1, out.pos, q * 8);
    out.slot[q] = holdSlot;
    out.uv[q * 4] = 0;
    out.uv[q * 4 + 1] = 1 - a1 / th;
    out.uv[q * 4 + 2] = 1;
    out.uv[q * 4 + 3] = 1;
    out.offscreen[q] = quadOffscreen(out.pos, q * 8) ? 1 : 0;
    lo = Math.min(lo, ex.headY0);
    hi = Math.max(hi, ex.headY1);
    q++;
  }
  writeCorners(le, fy, tx, yOffAr, sx, sy, -scale, scale, ex.tailY0, ex.tailY1, out.pos, q * 8);
  out.slot[q] = holdSlot;
  out.uv[q * 4] = 0;
  out.uv[q * 4 + 1] = 0;
  out.uv[q * 4 + 2] = 1;
  out.uv[q * 4 + 3] = a0 / th;
  out.offscreen[q] = quadOffscreen(out.pos, q * 8) ? 1 : 0;
  lo = Math.min(lo, ex.tailY0);
  hi = Math.max(hi, ex.tailY1);
  q++;
  out.quads = q;
  writeCorners(le, fy, tx, yOffAr, sx, sy, -scale, scale, lo, hi, out.outline, 0);
}
